// store/settingsSlice.ts
//
// Slimmed to a profile-persistence seam. Currency, learning goal, and the
// notification toggles were removed: none of them were read anywhere in the
// app (money always formats as USD via toolFormat; learningGoal had no
// consumers; the notification toggles were retired from the UI). What remains
// is the display-name persistence path used by the Settings Account section,
// with the same localStorage-now / API-later swap points.

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SettingsState {
	displayName: string;
	_lastUpdated: number;
	_syncStatus: 'idle' | 'pending' | 'synced' | 'error';
}

// ─── localStorage key ─────────────────────────────────────────────────────────

const STORAGE_KEY = 'nisk_settings';

function loadFromStorage(): Partial<SettingsState> | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

// ─── Default state ────────────────────────────────────────────────────────────

const DEFAULT_STATE: SettingsState = {
	displayName: '',
	_lastUpdated: Date.now(),
	_syncStatus: 'idle',
};

// Rebuild a clean state from whatever is in storage. Older payloads may still
// carry retired keys (currency / learningGoal / notifications); we read only
// the fields we still own, so those stale keys are ignored on load.
function buildInitialState(): SettingsState {
	const stored = loadFromStorage();
	return {
		displayName: stored?.displayName ?? DEFAULT_STATE.displayName,
		_lastUpdated: stored?._lastUpdated ?? Date.now(),
		_syncStatus: 'idle',
	};
}

// ─── Async thunks (swap bodies for API calls when .NET tier is ready) ─────────

/**
 * Currently reads from localStorage.
 * Swap for `api.get('/user/settings')` when the API is ready.
 */
export const fetchSettings = createAsyncThunk(
	'settings/fetch',
	async (_, { rejectWithValue }) => {
		try {
			const stored = loadFromStorage();
			const next: SettingsState = {
				displayName: stored?.displayName ?? DEFAULT_STATE.displayName,
				_lastUpdated: stored?._lastUpdated ?? Date.now(),
				_syncStatus: 'idle',
			};
			return next;
		} catch (err) {
			return rejectWithValue(`Failed to load settings, ${err}`);
		}
	},
);

/**
 * Currently persists to localStorage.
 * Swap for `api.put('/user/settings', data)` when the API is ready.
 *
 * Writes a clean object of only the fields we own, which also self-heals any
 * retired keys left over in an older persisted payload.
 */
export const saveSettings = createAsyncThunk(
	'settings/save',
	async (data: Partial<SettingsState>, { rejectWithValue }) => {
		try {
			const current = loadFromStorage();
			const next: SettingsState = {
				displayName:
					data.displayName ??
					current?.displayName ??
					DEFAULT_STATE.displayName,
				_lastUpdated: Date.now(),
				_syncStatus: 'synced',
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
			return next;
		} catch (err) {
			return rejectWithValue(`Failed to saved settings, ${err}`);
		}
	},
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const settingsSlice = createSlice({
	name: 'settings',
	initialState: buildInitialState(),
	reducers: {
		setDisplayName(state, action: PayloadAction<string>) {
			state.displayName = action.payload;
			state._lastUpdated = Date.now();
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchSettings.fulfilled, (state, action) => {
			return { ...state, ...action.payload, _syncStatus: 'idle' };
		});
		builder.addCase(saveSettings.pending, (state) => {
			state._syncStatus = 'pending';
		});
		builder.addCase(saveSettings.fulfilled, (_, action) => {
			return action.payload;
		});
		builder.addCase(saveSettings.rejected, (state) => {
			state._syncStatus = 'error';
		});
	},
});

export const { setDisplayName } = settingsSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export default settingsSlice.reducer;
